/**
 * PreAuthPanel — Anti-no-show security hold UI inside the chat view.
 *
 * Seller view:
 *   - No active hold → input amount + "Request Hold" button
 *   - requested → "Waiting for buyer to authorise…" + [Cancel Request]
 *   - held → "$X hold active" + final-price input + 3 outcome buttons:
 *       [Charge final price via wantd]   (Y == X / Y < X / Y > X all supported)
 *       [Released — paid off-platform]   (paid_cash / paid_other / cancelled / inspected_declined)
 *       [Buyer no-show]                  (captures full hold to wantd as fee)
 *   - captured / partial_captured / captured_no_show / remainder_*  → terminal badge
 *
 * Buyer view:
 *   - requested → amber banner + [Authorise Hold →] (links to /preauth/:id/confirm)
 *   - held → green status (no actions — seller drives)
 *   - remainder_action_required → amber banner + [Confirm top-up →]
 *   - captured / partial / no-show / remainder_failed → read-only badge
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, ShieldCheck, ShieldOff, ShieldAlert, Loader2, ChevronDown, ChevronUp, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from '../lib/toast';

export function PreAuthPanel({ matchId, matchInfo, currentUser, api }) {
  const navigate = useNavigate();
  const [preauth, setPreauth] = useState(undefined); // undefined = loading
  const [expanded, setExpanded] = useState(false);
  const [amount, setAmount] = useState('');
  const [requesting, setRequesting] = useState(false);
  const [acting, setActing] = useState(false);
  const [showReleaseConfirm, setShowReleaseConfirm] = useState(false);
  const [finalPrice, setFinalPrice] = useState('');

  const isSeller = matchInfo?.owner_id === currentUser?.id;
  const isBuyer = matchInfo?.wanter_id === currentUser?.id;

  const fetchPreauth = useCallback(async () => {
    if (!matchId) return;
    try {
      const res = await api.get(`/preauth/match/${matchId}`);
      setPreauth(res.data.preauth);
      // Auto-expand for buyer when a hold is requested
      if (res.data.preauth?.status === 'requested' && !isSeller) {
        setExpanded(true);
      }
    } catch {
      setPreauth(null);
    }
  }, [matchId, api, isSeller]);

  useEffect(() => {
    fetchPreauth();
  }, [fetchPreauth]);

  // Poll while a hold is in flight so seller sees state changes (held / remainder_pending → captured)
  useEffect(() => {
    const pollable = ['requested', 'remainder_pending'];
    if (!preauth || !pollable.includes(preauth.status) || !isSeller) return;
    const interval = setInterval(async () => {
      try {
        const res = await api.get(`/preauth/match/${matchId}`);
        const updated = res.data.preauth;
        if (updated?.status !== preauth.status) {
          setPreauth(updated);
        }
      } catch {
        // silent — don't break the UI on a poll failure
      }
    }, 8000); // every 8 seconds
    return () => clearInterval(interval);
  }, [preauth, isSeller, api, matchId]);

  // Suggest item price as default amount for seller
  useEffect(() => {
    if (isSeller && !amount) {
      const price = matchInfo?.inventory_price || matchInfo?.offer_amount_cents;
      if (price) setAmount((price / 100).toFixed(2));
    }
  }, [isSeller, matchInfo, amount]);

  // Default the final-price input to the hold amount whenever a hold becomes active
  useEffect(() => {
    if (preauth?.status === 'held' && !finalPrice) {
      setFinalPrice((preauth.amount_cents / 100).toFixed(2));
    }
  }, [preauth?.status, preauth?.amount_cents, finalPrice]);

  const handleRequest = async () => {
    const cents = Math.round(parseFloat(amount) * 100);
    if (!cents || cents < 100) {
      toast.error('Minimum hold amount is $1.00');
      return;
    }
    setRequesting(true);
    try {
      const res = await api.post('/preauth/request', {
        match_id: matchId,
        amount_cents: cents,
      });
      setPreauth(res.data);
      toast.success('Security hold requested — waiting for buyer to authorise');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to request hold');
    } finally {
      setRequesting(false);
    }
  };

  const handleCharge = async () => {
    if (!preauth) return;
    const cents = Math.round(parseFloat(finalPrice) * 100);
    if (!cents || cents < 100) {
      toast.error('Minimum charge is $1.00');
      return;
    }
    const holdCents = preauth.amount_cents;
    if (cents > holdCents) {
      const extra = ((cents - holdCents) / 100).toFixed(2);
      const ok = window.confirm(
        `This will capture $${(holdCents / 100).toFixed(2)} from the hold ` +
        `and charge an additional $${extra} to the buyer's saved card. Continue?`
      );
      if (!ok) return;
    }
    setActing(true);
    try {
      const res = await api.post(`/preauth/${preauth.id}/finalize`, {
        outcome: 'charge',
        final_amount_cents: cents,
      });
      setPreauth(res.data);
      const status = res.data.status;
      if (status === 'captured') toast.success('Payment captured — paid in full');
      else if (status === 'partial_captured') toast.success('Partial payment captured; remainder released');
      else if (status === 'remainder_pending') toast.success('Hold captured; top-up in flight');
      else if (status === 'remainder_action_required') toast.success('Buyer needs to confirm top-up with their bank');
      else if (status === 'remainder_failed') toast.error('Top-up failed — captured portion credited');
      else toast.success('Payment finalized');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to charge buyer');
    } finally {
      setActing(false);
    }
  };

  const handleNoShow = async () => {
    if (!preauth) return;
    const dollars = (preauth.amount_cents / 100).toFixed(2);
    const ok = window.confirm(`Charge $${dollars} no-show fee? The buyer will be charged immediately.`);
    if (!ok) return;
    setActing(true);
    try {
      const res = await api.post(`/preauth/${preauth.id}/finalize`, { outcome: 'no_show' });
      setPreauth(res.data);
      toast.success(`$${dollars} no-show fee charged`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to charge no-show fee');
    } finally {
      setActing(false);
    }
  };

  const handleRelease = async (reason) => {
    if (!preauth) return;
    setActing(true);
    setShowReleaseConfirm(false);
    try {
      const res = await api.post(`/preauth/${preauth.id}/release`, { reason });
      setPreauth(res.data);
      toast.success('Hold released — no charge to buyer');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to release hold');
    } finally {
      setActing(false);
    }
  };

  // Don't render if loading or no match info
  if (preauth === undefined || !matchInfo) return null;

  // For buyer: don't show panel at all if no active preauth
  if (isBuyer && !preauth) return null;
  const buyerVisibleStatuses = [
    'requested', 'held', 'captured', 'partial_captured', 'captured_no_show',
    'remainder_pending', 'remainder_action_required', 'remainder_failed', 'released',
  ];
  if (isBuyer && preauth && !buyerVisibleStatuses.includes(preauth.status)) return null;

  const amountDisplay = preauth ? `$${(preauth.amount_cents / 100).toFixed(2)}` : null;
  const finalDisplay = preauth?.final_amount_cents != null
    ? `$${(preauth.final_amount_cents / 100).toFixed(2)}`
    : null;
  const remainderDisplay = preauth?.remainder_cents != null
    ? `$${(preauth.remainder_cents / 100).toFixed(2)}`
    : null;

  // ── Buyer panel ──────────────────────────────────────────────────────────────
  if (isBuyer) {
    if (preauth.status === 'requested') {
      return (
        <div className="mx-4 mb-3 rounded-xl border border-amber-200 bg-amber-50 dark:bg-amber-900/10 dark:border-amber-700/40 p-3">
          <div className="flex items-start gap-2.5">
            <Shield className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">
                Security hold requested — {amountDisplay}
              </p>
              <p className="text-xs text-amber-700 dark:text-amber-400 mt-0.5">
                @{matchInfo.owner_username} wants to place a {amountDisplay} hold on your card to
                confirm the meetup. The seller may charge up to your agreed price after the meetup
                — they cannot charge more than that without your confirmation.
              </p>
              <Button
                onClick={() => navigate(`/preauth/${preauth.id}/confirm?match=${matchId}`)}
                className="mt-2 h-8 text-xs bg-amber-600 hover:bg-amber-700 text-white"
              >
                <Shield className="w-3.5 h-3.5 mr-1.5" />
                Authorise Hold
              </Button>
            </div>
          </div>
        </div>
      );
    }

    if (preauth.status === 'held') {
      return (
        <div className="mx-4 mb-3 rounded-xl border border-green-200 bg-green-50 dark:bg-green-900/10 dark:border-green-700/40 p-3">
          <div className="flex items-center gap-2.5">
            <ShieldCheck className="w-4 h-4 text-green-600 dark:text-green-400 flex-shrink-0" />
            <p className="text-sm text-green-800 dark:text-green-300">
              <span className="font-semibold">{amountDisplay} hold active</span>
              {' '}— funds reserved, no charge yet
            </p>
          </div>
        </div>
      );
    }

    if (preauth.status === 'remainder_action_required') {
      return (
        <div className="mx-4 mb-3 rounded-xl border border-amber-200 bg-amber-50 dark:bg-amber-900/10 dark:border-amber-700/40 p-3">
          <div className="flex items-start gap-2.5">
            <ShieldAlert className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">
                Confirm top-up — {remainderDisplay}
              </p>
              <p className="text-xs text-amber-700 dark:text-amber-400 mt-0.5">
                Your bank needs you to confirm the {remainderDisplay} top-up charge.
              </p>
              <Button
                onClick={() => navigate(`/preauth/${preauth.id}/remainder/confirm`)}
                className="mt-2 h-8 text-xs bg-amber-600 hover:bg-amber-700 text-white"
              >
                <ShieldAlert className="w-3.5 h-3.5 mr-1.5" />
                Confirm Top-up
              </Button>
            </div>
          </div>
        </div>
      );
    }

    if (preauth.status === 'remainder_pending') {
      return (
        <div className="mx-4 mb-3 rounded-xl border border-amber-200 bg-amber-50 dark:bg-amber-900/10 dark:border-amber-700/40 p-3">
          <div className="flex items-center gap-2.5">
            <Loader2 className="w-4 h-4 text-amber-600 dark:text-amber-400 animate-spin flex-shrink-0" />
            <p className="text-sm text-amber-800 dark:text-amber-300">
              <span className="font-semibold">{finalDisplay} charge in flight</span>
              {' '}— confirming with your bank
            </p>
          </div>
        </div>
      );
    }

    if (preauth.status === 'captured') {
      return (
        <div className="mx-4 mb-3 rounded-xl border border-[#1E3A5F]/20 bg-[#1E3A5F]/5 p-3">
          <div className="flex items-center gap-2.5">
            <ShieldCheck className="w-4 h-4 text-[#1E3A5F] dark:text-blue-300 flex-shrink-0" />
            <p className="text-sm text-[#1E3A5F] dark:text-blue-200">
              <span className="font-semibold">{finalDisplay || amountDisplay} charged</span>
              {' '}— deal complete
            </p>
          </div>
        </div>
      );
    }

    if (preauth.status === 'partial_captured') {
      const released = preauth.amount_cents - (preauth.final_amount_cents || 0);
      return (
        <div className="mx-4 mb-3 rounded-xl border border-[#1E3A5F]/20 bg-[#1E3A5F]/5 p-3">
          <div className="flex items-center gap-2.5">
            <ShieldCheck className="w-4 h-4 text-[#1E3A5F] dark:text-blue-300 flex-shrink-0" />
            <p className="text-sm text-[#1E3A5F] dark:text-blue-200">
              <span className="font-semibold">{finalDisplay} charged</span>
              {' '}— ${(released / 100).toFixed(2)} released back to your card
            </p>
          </div>
        </div>
      );
    }

    if (preauth.status === 'captured_no_show') {
      return (
        <div className="mx-4 mb-3 rounded-xl border border-red-200 bg-red-50 dark:bg-red-900/10 dark:border-red-700/40 p-3">
          <div className="flex items-center gap-2.5">
            <ShieldAlert className="w-4 h-4 text-red-600 dark:text-red-400 flex-shrink-0" />
            <p className="text-sm text-red-800 dark:text-red-300">
              <span className="font-semibold">{amountDisplay} no-show fee charged</span>
            </p>
          </div>
        </div>
      );
    }

    if (preauth.status === 'remainder_failed') {
      const captured = preauth.amount_cents;
      return (
        <div className="mx-4 mb-3 rounded-xl border border-red-200 bg-red-50 dark:bg-red-900/10 dark:border-red-700/40 p-3">
          <div className="flex items-center gap-2.5">
            <ShieldAlert className="w-4 h-4 text-red-600 dark:text-red-400 flex-shrink-0" />
            <p className="text-sm text-red-800 dark:text-red-300">
              <span className="font-semibold">${(captured / 100).toFixed(2)} charged</span>
              {' '}— top-up of {remainderDisplay} failed
            </p>
          </div>
        </div>
      );
    }

    if (preauth.status === 'released') {
      return (
        <div className="mx-4 mb-3 rounded-xl border border-[var(--theme-border)] bg-[var(--theme-hover)] p-3">
          <div className="flex items-center gap-2.5">
            <ShieldOff className="w-4 h-4 text-[var(--theme-text-muted)] flex-shrink-0" />
            <p className="text-sm text-[var(--theme-text-muted)]">
              Security hold released — no charge
            </p>
          </div>
        </div>
      );
    }

    return null;
  }

  // ── Seller panel ─────────────────────────────────────────────────────────────
  if (!isSeller) return null;

  // Terminal states — compact read-only badge
  const terminalStates = [
    'captured', 'partial_captured', 'captured_no_show',
    'remainder_failed', 'released', 'expired',
  ];
  if (preauth && terminalStates.includes(preauth.status)) {
    const releasedCents = preauth.status === 'partial_captured'
      ? preauth.amount_cents - (preauth.final_amount_cents || 0)
      : 0;
    const remainderCents = preauth.remainder_cents || 0;
    const sellerCredited = preauth.seller_credited_cents || 0;

    const config = {
      captured: {
        Icon: ShieldCheck,
        cls: 'border-[#1E3A5F]/20 bg-[#1E3A5F]/5 text-[#1E3A5F] dark:text-blue-300',
        label: `${finalDisplay || amountDisplay} charged — paid in full via wantd`,
      },
      partial_captured: {
        Icon: ShieldCheck,
        cls: 'border-[#1E3A5F]/20 bg-[#1E3A5F]/5 text-[#1E3A5F] dark:text-blue-300',
        label: `${finalDisplay} charged — $${(releasedCents / 100).toFixed(2)} released`,
      },
      captured_no_show: {
        Icon: ShieldAlert,
        cls: 'border-red-200 bg-red-50 text-red-700 dark:bg-red-900/10 dark:border-red-700/40 dark:text-red-300',
        label: `${amountDisplay} no-show fee charged`,
      },
      remainder_failed: {
        Icon: AlertCircle,
        cls: 'border-red-200 bg-red-50 text-red-700 dark:bg-red-900/10 dark:border-red-700/40 dark:text-red-300',
        label: `$${(sellerCredited / 100).toFixed(2)} captured — top-up of $${(remainderCents / 100).toFixed(2)} failed, follow up with @${matchInfo.wanter_username}`,
      },
      released: {
        Icon: ShieldOff,
        cls: 'border-[var(--theme-border)] bg-[var(--theme-hover)] text-[var(--theme-text-muted)]',
        label: `${amountDisplay} hold released`,
      },
      expired: {
        Icon: ShieldOff,
        cls: 'border-[var(--theme-border)] bg-[var(--theme-hover)] text-[var(--theme-text-muted)]',
        label: 'Hold expired',
      },
    }[preauth.status];

    const Icon = config.Icon;
    return (
      <div className={`mx-4 mb-3 rounded-xl border p-3 ${config.cls}`}>
        <div className="flex items-center gap-2.5">
          <Icon className="w-4 h-4 flex-shrink-0" />
          <p className="text-sm font-semibold">{config.label}</p>
        </div>
      </div>
    );
  }

  // Remainder in flight — informational (no actions for seller until webhook resolves)
  if (preauth?.status === 'remainder_pending') {
    return (
      <div className="mx-4 mb-3 rounded-xl border border-amber-200 bg-amber-50 dark:bg-amber-900/10 dark:border-amber-700/40 p-3">
        <div className="flex items-start gap-2.5">
          <Loader2 className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 animate-spin flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">
              {finalDisplay} in flight — confirming with bank
            </p>
            <p className="text-xs text-amber-700 dark:text-amber-400 mt-0.5">
              {amountDisplay} captured. Top-up of {remainderDisplay} is being processed off-session.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // SCA required — buyer needs to confirm
  if (preauth?.status === 'remainder_action_required') {
    return (
      <div className="mx-4 mb-3 rounded-xl border border-amber-200 bg-amber-50 dark:bg-amber-900/10 dark:border-amber-700/40 p-3">
        <div className="flex items-start gap-2.5">
          <ShieldAlert className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">
              Buyer needs to confirm the {remainderDisplay} top-up
            </p>
            <p className="text-xs text-amber-700 dark:text-amber-400 mt-0.5">
              @{matchInfo.wanter_username}'s bank requires confirmation. {amountDisplay} has been
              captured to your wallet; the top-up will land once confirmed.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (preauth?.status === 'requested') {
    return (
      <div className="mx-4 mb-3 rounded-xl border border-amber-200 bg-amber-50 dark:bg-amber-900/10 dark:border-amber-700/40 p-3">
        <div className="flex items-start gap-2.5">
          <Loader2 className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 animate-spin flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">
              Waiting for @{matchInfo.wanter_username} to authorise {amountDisplay} hold…
            </p>
            <p className="text-xs text-amber-700 dark:text-amber-400 mt-0.5">
              A notification has been sent. You can cancel the request below.
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleRelease('cancelled')}
              disabled={acting}
              className="mt-2 h-7 text-xs border-amber-400 text-amber-700 hover:bg-amber-100 dark:text-amber-300 dark:border-amber-600"
            >
              {acting ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
              Cancel Request
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (preauth?.status === 'held') {
    const holdCents = preauth.amount_cents;
    const finalCents = Math.round(parseFloat(finalPrice || '0') * 100);
    const validFinal = finalCents >= 100;

    let helpText = '';
    if (validFinal) {
      if (finalCents === holdCents) {
        helpText = `Captures the ${amountDisplay} hold.`;
      } else if (finalCents < holdCents) {
        const released = (holdCents - finalCents) / 100;
        helpText = `Captures $${(finalCents / 100).toFixed(2)}; the remaining $${released.toFixed(2)} is released back to the buyer.`;
      } else {
        const extra = (finalCents - holdCents) / 100;
        helpText = `Captures ${amountDisplay} hold and charges an extra $${extra.toFixed(2)} to the same card. Buyer's bank may ask them to confirm.`;
      }
    }

    return (
      <div className="mx-4 mb-3 rounded-xl border border-green-200 bg-green-50 dark:bg-green-900/10 dark:border-green-700/40 p-3">
        <div className="flex items-start gap-2.5">
          <ShieldCheck className="w-4 h-4 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-green-800 dark:text-green-300">
              {amountDisplay} hold authorised
            </p>
            <p className="text-xs text-green-700 dark:text-green-500 mt-0.5">
              @{matchInfo.wanter_username}'s card is reserved. What happened?
            </p>

            {/* Charge final price block */}
            <div className="mt-3 space-y-2">
              <label className="text-xs font-medium text-green-800 dark:text-green-300 block">
                Final price
              </label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-[var(--theme-text-muted)]">$</span>
                  <input
                    type="number"
                    min="1"
                    step="0.01"
                    value={finalPrice}
                    onChange={e => setFinalPrice(e.target.value)}
                    placeholder="0.00"
                    disabled={acting}
                    className="w-full pl-6 pr-2 py-1.5 text-sm rounded-lg border border-green-300 bg-white dark:bg-[var(--theme-card)] text-[var(--theme-text-primary)] focus:outline-none focus:ring-1 focus:ring-green-600"
                  />
                </div>
                <Button
                  size="sm"
                  onClick={handleCharge}
                  disabled={acting || !validFinal}
                  className="h-8 text-xs bg-green-700 hover:bg-green-800 text-white shrink-0"
                >
                  {acting
                    ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    : <><ShieldCheck className="w-3.5 h-3.5 mr-1" /> Charge via wantd</>}
                </Button>
              </div>
              {helpText && (
                <p className="text-[11px] text-green-700 dark:text-green-500 leading-snug">{helpText}</p>
              )}
            </div>

            {/* Other outcomes */}
            <div className="mt-3 flex gap-2 flex-wrap">
              {showReleaseConfirm ? (
                <div className="flex flex-col gap-1.5 w-full">
                  <p className="text-xs text-green-700 dark:text-green-400 font-medium">Why are you releasing?</p>
                  <div className="flex gap-1.5 flex-wrap">
                    <Button
                      size="sm"
                      onClick={() => handleRelease('paid_cash')}
                      disabled={acting}
                      className="h-7 text-xs bg-[#1E3A5F] hover:bg-[#2D4A6F] text-white"
                    >
                      Paid cash
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => handleRelease('paid_other')}
                      disabled={acting}
                      className="h-7 text-xs bg-[#1E3A5F] hover:bg-[#2D4A6F] text-white"
                    >
                      Paid off-platform
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => handleRelease('inspected_declined')}
                      disabled={acting}
                      className="h-7 text-xs bg-[var(--theme-card)] border border-[var(--theme-border)] text-[var(--theme-text-primary)] hover:bg-[var(--theme-hover)]"
                    >
                      Showed up, didn't buy
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => handleRelease('cancelled')}
                      disabled={acting}
                      className="h-7 text-xs bg-[var(--theme-card)] border border-[var(--theme-border)] text-[var(--theme-text-primary)] hover:bg-[var(--theme-hover)]"
                    >
                      Cancelled
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowReleaseConfirm(false)}
                      className="h-7 text-xs"
                    >
                      Back
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowReleaseConfirm(true)}
                    disabled={acting}
                    className="h-7 text-xs border-green-400 text-green-700 hover:bg-green-100 dark:text-green-300 dark:border-green-600"
                  >
                    <ShieldOff className="w-3 h-3 mr-1" />
                    Released — paid off-platform
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleNoShow}
                    disabled={acting}
                    className="h-7 text-xs bg-red-600 hover:bg-red-700 text-white"
                  >
                    <ShieldAlert className="w-3 h-3 mr-1" />
                    Buyer no-show
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // No active preauth — seller can request one
  return (
    <div className="mx-4 mb-3 rounded-xl border border-[var(--theme-border)] bg-[var(--theme-hover)]">
      <button
        onClick={() => setExpanded(e => !e)}
        className="w-full flex items-center justify-between px-3 py-2.5 text-left"
      >
        <div className="flex items-center gap-2">
          <Shield className="w-4 h-4 text-[var(--theme-text-muted)]" />
          <span className="text-xs font-medium text-[var(--theme-text-secondary)]">
            Request Security Hold
          </span>
        </div>
        {expanded
          ? <ChevronUp className="w-3.5 h-3.5 text-[var(--theme-text-muted)]" />
          : <ChevronDown className="w-3.5 h-3.5 text-[var(--theme-text-muted)]" />}
      </button>

      {expanded && (
        <div className="px-3 pb-3 border-t border-[var(--theme-border)]">
          <p className="text-xs text-[var(--theme-text-muted)] mt-2 mb-2.5">
            Place a hold on the buyer's card to confirm the meetup.
            Charge the final price via wantd, release if they paid off-platform,
            or charge the full hold as a no-show fee.
          </p>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-[var(--theme-text-muted)]">$</span>
              <input
                type="number"
                min="1"
                step="0.01"
                value={amount}
                onChange={e => setAmount(e.target.value)}
                placeholder="0.00"
                className="w-full pl-6 pr-2 py-1.5 text-sm rounded-lg border border-[var(--theme-border)] bg-[var(--theme-card)] text-[var(--theme-text-primary)] focus:outline-none focus:ring-1 focus:ring-[#1E3A5F]"
              />
            </div>
            <Button
              size="sm"
              onClick={handleRequest}
              disabled={requesting || !amount}
              className="h-8 text-xs bg-[#1E3A5F] hover:bg-[#2D4A6F] text-white shrink-0"
            >
              {requesting
                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                : <>
                    <Shield className="w-3.5 h-3.5 mr-1" />
                    Request
                  </>}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
